<template>
  <div>
    <Navbar type="bg-transparent-sticky" />
    <div class="hero-container-thin">
      <div class="hero-opacity" />
      <img class="hero-image" src="../assets/gdq_hero.jpg" alt="hero image" />
    </div>
    <Breadcrumb />
    <div class="container-xl" style="position: relative !important;">
      <div v-if="loading" class="loading-overlay d-flex justify-content-center mb-3">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div class="text-container">
        <div class="title-area">
          <div class="d-flex">
            <h1 class="page-title h2">
              <span id="set-information">
                {{ screentexts[7] ? screentexts[7].element_text : '' }}
              </span>
            </h1>
          </div>
        </div>
        <p>{{ screentexts[8] ? screentexts[8].element_text : 'Before setting up your survey, tell us about the Group!' }}</p>
        <div class="form-group">
          <label>{{ screentexts[9] ? screentexts[9].element_text : 'Give your group a name' }}</label>
          <input
            type="text"
            class="form-control"
            :class="anon ? 'g-anon' : ''"
            v-model="group.group_name"
            placeholder="Group name"
          />
        </div>
        <div class="form-group">
          <label>{{ screentexts[10] ? screentexts[10].element_text : 'Save to folder' }}</label>
          <select class="form-select" v-model="group.group_folder_id">
            <option v-for="option in folderOptions" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
          <small class="form-text">
            {{ screentexts[10] ? screentexts[10].element_bubbletext : 'Do you want to put your group in a folder? Tell us where!' }}
          </small>
        </div>
        <div class="form-group">
          <label>{{ screentexts[11] ? screentexts[11].element_text : 'Group language' }}</label>
          <select class="form-select" v-model="group.group_language_id">
            <option v-for="option in languageOptions" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
          <small class="form-text">
            {{ screentexts[11] ? screentexts[11].element_bubbletext : 'Which language would you like to use for this group? You can change this later.' }}
          </small>
        </div>
        <div class="form-group">
          <label>{{ screentexts[12] ? screentexts[12].element_text : 'Norm values' }}</label>
          <select class="form-select" v-model="group.group_normvalues_id">
            <option v-for="option in normValuesOptions" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
          <small class="form-text">
            {{ screentexts[12] ? screentexts[12].element_bubbletext : 'Which set of norm values do you want to use for this group? You can change this later.' }}
          </small>
        </div>
        <div class="row">&nbsp;</div>
        <div class="d-flex justify-content-between w-100 mb-5">
          <router-link to="/create" class="btn btn-secondary float-left">
            &lt;<span class="d-none d-sm-inline"> Back</span>
          </router-link>
          <router-link v-if="canProceed" to="/create/group/members" class="float-right">
            <button class="btn btn-primary">
              <span class="d-none d-sm-inline">Next </span>&gt;
            </button>
          </router-link>
          <button v-else disabled class="btn btn-primary disabled float-right">
            <span class="d-none d-sm-inline">Next </span>&gt;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { onBeforeRouteLeave } from 'vue-router'
import Navbar from './Navbar.vue'
import Breadcrumb from './Breadcrumb.vue'

defineOptions({ name: 'CreateGroup' })

const store = useStore()
const loading = ref(false)
const screenId = 3
const screentexts = ref([])

const group = reactive({
  user_id: store.state.user_id,
  id: store.state.currentgroup.id,
  group_folder_id: store.state.currentgroup.group_folder_id,
  group_name: store.state.currentgroup.group_name,
  group_language_id: store.state.currentgroup.group_language_id,
  group_normvalues_id: store.state.currentgroup.group_normvalues_id
})

const anon = computed(() => store.state.anon)

const folderOptions = computed(() => {
  const items = store.state.folders || []
  const options = [{ value: 0, text: 'No folder' }]
  items.forEach(item => options.push({ value: item.id, text: item.folder_name }))
  return options
})

const languageOptions = computed(() => {
  const items = store.state.languages || []
  const options = [{ value: 0, text: 'Please select a language' }]
  items.forEach(item => options.push({ value: item.id, text: item.language_name }))
  return options
})

const normValuesOptions = computed(() => {
  const items = store.state.normvalues_countries || []
  const options = [{ value: 0, text: 'Please select norm values' }]
  items.forEach(item => options.push({ value: item.country_id, text: item.country_name }))
  return options
})

const canProceed = computed(() => {
  return group.group_name !== '' && Number(group.group_language_id) !== 0 && Number(group.group_normvalues_id) !== 0
})

const fetchFolders = () => store.dispatch('folders_fetch')
const fetchLanguages = () => store.dispatch('languages_fetch')
const fetchNormValues = () => store.dispatch('normvalues_countries_fetch')

const group_create = () => store.dispatch('group_create', { group })
const group_save = () => store.dispatch('group_save', { group })

const fetchScreenTexts = () => {
  const filter = {
    screen_id: {
      operator: '=',
      value: screenId
    }
  }
  return store.dispatch('systemscreenelements_fetch', { filter }).then((resp) => {
    const dbtexts = resp.data.result || []
    const mapped = []
    dbtexts.forEach((item) => {
      mapped[item.id] = {
        id: item.id,
        element_text: item.element_text,
        element_bubbletext: item.element_bubbletext
      }
    })
    screentexts.value = mapped
  })
}

onMounted(() => {
  fetchFolders()
  fetchScreenTexts()
  fetchLanguages()
  fetchNormValues()
})

onBeforeRouteLeave(async (to, from, next) => {
  if (to.name === 'creategroupmembers') {
    loading.value = true
    try {
      if (store.state.currentgroup.id === 0) {
        await group_create()
      } else {
        await group_save()
      }
    } finally {
      loading.value = false
    }
  }
  next()
})
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
</style>
